// Web push (VAPID) opt-in for IhsanOne.
//
// One responsibility: turn a signed-in member's browser into a live push
// endpoint recorded in the RLS-protected `push_subscriptions` table.
//
// Trust boundaries:
//   * The VAPID PUBLIC key is baked into the bundle — it is public by design
//     (browsers use it to bind the endpoint to our sender's private key). The
//     PRIVATE key lives only on the server-side sender in Bitwarden.
//   * The Supabase anon key + the caller's own JWT are the only credentials
//     used here. All writes go through the SECURITY DEFINER RPCs
//     `push_subscribe` / `push_unsubscribe` — RLS guarantees a caller cannot
//     see or delete another user's endpoint even by guessing an id.
//
// State model returned by `getPushState()`:
//   'unsupported'    Push API / SW / notifications missing (older browser,
//                    Safari <16.4, in-app browser, private mode etc.).
//   'blocked'        User picked "Block" in the browser permission prompt.
//   'granted-off'    Permission already granted, but no active subscription
//                    (rare — happens after a browser reset).
//   'granted-on'     Permission granted AND we have an active subscription.
//   'default'        Permission not yet asked — we can prompt.

import { getSession, getAccessToken, SUPABASE_ANON } from './session';

const SUPABASE_URL = (process.env.REACT_APP_SUPABASE_URL
  || 'https://yuftrqcahapyrblxvkns.supabase.co').replace(/\/$/, '');

// VAPID public key: baked-in default matches what the server sender uses.
// If we ever rotate keys, ship a bundle with a new default and it will win
// over the old server-registered subscriptions gracefully (old endpoints
// still work until they naturally expire; new subscribes bind to the new key).
export const VAPID_PUBLIC_KEY = (process.env.REACT_APP_VAPID_PUBLIC_KEY
  || 'BLKOeoFO-UssqWWkX8C0CgF54yla9-Q9r36YXhLaKRdyzNpgWznc7TXcqLfU792CCPgBx4u3jH62zqmNvHbpmnE');

export function isPushSupported() {
  if (typeof window === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!('PushManager' in window)) return false;
  if (!('Notification' in window)) return false;
  return true;
}

/** VAPID applicationServerKey conversion — base64url -> Uint8Array. */
function urlBase64ToUint8Array(base64url) {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const b64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

function abToB64Url(buffer) {
  const bytes = new Uint8Array(buffer);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
}

async function getRegistration() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const ready = await navigator.serviceWorker.ready;
    return ready || (await navigator.serviceWorker.getRegistration()) || null;
  } catch {
    return null;
  }
}

export async function getPushState() {
  if (!isPushSupported()) return { state: 'unsupported', subscription: null };
  const permission = Notification.permission;
  if (permission === 'denied') return { state: 'blocked', subscription: null };
  const reg = await getRegistration();
  if (!reg) return { state: permission === 'granted' ? 'granted-off' : 'default', subscription: null };
  const sub = await reg.pushManager.getSubscription().catch(() => null);
  if (permission === 'granted') {
    return { state: sub ? 'granted-on' : 'granted-off', subscription: sub };
  }
  return { state: 'default', subscription: sub };
}

function subscriptionToRow(sub) {
  const json = sub.toJSON();
  return {
    endpoint: json.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
  };
}

async function callRpc(fn, body) {
  // getSession() returns the compact snapshot (name/email/uid/meta) —
  // deliberately no access_token in there. Use getAccessToken() which
  // reads the raw session and refreshes if the token is about to expire.
  const access_token = await getAccessToken();
  if (!access_token) throw new Error('not_authenticated');
  const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${fn}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: SUPABASE_ANON,
      Authorization: `Bearer ${access_token}`,
    },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  if (!res.ok) {
    let msg = text;
    try { msg = JSON.parse(text)?.message || msg; } catch {}
    const err = new Error(msg || `rpc_failed_${res.status}`);
    err.status = res.status;
    throw err;
  }
  try { return JSON.parse(text); } catch { return text; }
}

/**
 * Subscribe the current member's browser. Returns the resulting state object
 * on success. Errors bubble up so the UI can show a specific message; the
 * caller is responsible for having verified permission is not 'denied'.
 */
export async function subscribePush() {
  if (!isPushSupported()) throw new Error('unsupported');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    const err = new Error(permission === 'denied' ? 'blocked' : 'dismissed');
    err.code = permission;
    throw err;
  }
  const reg = await getRegistration();
  if (!reg) throw new Error('sw_not_ready');

  let sub = await reg.pushManager.getSubscription().catch(() => null);
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }

  const row = subscriptionToRow(sub);
  await callRpc('push_subscribe', {
    p_endpoint: row.endpoint,
    p_p256dh: row.p256dh,
    p_auth: row.auth,
    p_ua: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  });

  return { state: 'granted-on', subscription: sub };
}

/**
 * Unsubscribe both the browser-side push channel AND the row in Supabase.
 * Best-effort: if the browser hangs on to a stale sub after we tear the row
 * down, the sender will still 410 on it and we'll refresh via
 * pushsubscriptionchange. Fully server-authoritative — the DB row is the
 * only source of truth for who receives pushes.
 */
export async function unsubscribePush() {
  if (!isPushSupported()) return { state: 'unsupported', subscription: null };
  const reg = await getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription().catch(() => null) : null;
  const endpoint = sub?.endpoint;
  // Server first — even if the browser fails to tear down its side, we stop
  // delivering. If we did it the other way round and the RPC failed, the
  // browser would still be listed and the sender would keep trying.
  if (endpoint) {
    try { await callRpc('push_unsubscribe', { p_endpoint: endpoint }); } catch (e) {
      if (e.status && e.status !== 404) throw e;
    }
  }
  if (sub) {
    try { await sub.unsubscribe(); } catch { /* browser detachment best-effort */ }
  }
  return { state: 'default', subscription: null };
}

/**
 * Record a real-device notification click server-side. Requires an active
 * Supabase session (the RPC refuses anonymous callers with 'not_authenticated'
 * — we short-circuit here so signed-out taps don't produce a noisy 401 on
 * the network tab and don't drop the deep link on the floor either).
 *
 * `ptrace` is the per-send UUID the sender embedded in the push payload;
 * pairing a click with its ptrace lets ops verify a real tap on a real
 * device (no CDP injection, no SW-message hook) came back through the DB.
 */
export async function recordPushClick({ tag, url, ptrace, userAgent } = {}) {
  const access_token = await getAccessToken().catch(() => null);
  if (!access_token) {
    // Signed-out click: skip the RPC (would just 401). The deep link still
    // works — the URL query param is stripped by the recorder either way.
    return { ok: false, reason: 'not_authenticated' };
  }
  try {
    const res = await callRpc('record_push_click', {
      p_tag: tag || null,
      p_url: url || null,
      p_ptrace: ptrace || null,
      p_user_agent: userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : null),
    });
    return { ok: true, ...res };
  } catch (e) {
    return { ok: false, reason: e?.message || 'rpc_failed', status: e?.status };
  }
}

// Test-only helper — the E2E harness reads this to grab an endpoint+keys
// without needing to poke into internals. NOT called from prod UI.
export async function _debugCurrentSubscription() {
  const reg = await getRegistration();
  if (!reg) return null;
  const sub = await reg.pushManager.getSubscription().catch(() => null);
  if (!sub) return null;
  const raw = sub.toJSON();
  // Expose keys as base64url (already the shape toJSON gives us).
  return {
    endpoint: raw.endpoint,
    p256dh: raw.keys?.p256dh,
    auth: raw.keys?.auth,
    expirationTime: raw.expirationTime || null,
  };
}

// Exposed on window in tests only.
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  window.__ihsanonePush = {
    getPushState, subscribePush, unsubscribePush, _debug: _debugCurrentSubscription,
  };
}

// Small helper — also exposes it in production but under a namespaced key so
// the E2E harness can call it. This is READ-ONLY; the real writes still go
// through RLS+RPC using the caller's own JWT.
if (typeof window !== 'undefined') {
  window.__ihsanoneReadPush = _debugCurrentSubscription;
}
